// MUNYA OS · Cheat-sheet de atajos · § 18.4-11 · A3
//
// Modal centrado disparado por `?`. Lista los atajos agrupados por categoría.
// Se cierra con ESC, clic fuera o tecla `?` de nuevo.

import { useEffect, useState, type ReactElement } from 'react';
import { Keyboard, X } from '@phosphor-icons/react';
import { keyboardShortcuts, SHORTCUT_CHEATSHEET } from '../lib/keyboard-shortcuts.js';
import { useDismissable } from '../lib/use-dismissable.js';

export function ShortcutsCheatSheet(): ReactElement | null {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    return keyboardShortcuts.onCheatSheetToggle(() => {
      setOpen((o) => !o);
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // · #456 · el fondo solo cierra con el gesto completo (mousedown + mouseup sobre el fondo)
  const { backdropProps } = useDismissable({
    onDismiss: () => {
      setOpen(false);
    },
  });

  if (!open) return null;

  const grouped = new Map<string, typeof SHORTCUT_CHEATSHEET>();
  for (const s of SHORTCUT_CHEATSHEET) {
    const arr = grouped.get(s.category) ?? [];
    arr.push(s);
    grouped.set(s.category, arr);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Atajos de teclado"
      {...backdropProps}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[var(--color-munya-coffee)]/60 backdrop-blur-sm"
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="bg-[var(--color-munya-surface)] rounded-lg shadow-2xl w-full max-w-lg p-6 border border-[var(--color-munya-sand)] max-h-[85vh] overflow-y-auto"
      >
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Keyboard size={18} weight="duotone" className="text-[var(--color-munya-olive)]" />
            <h2 className="font-semibold">Atajos de teclado</h2>
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
            }}
            aria-label="Cerrar"
            className="opacity-60 hover:opacity-100"
          >
            <X size={16} />
          </button>
        </header>
        <div className="space-y-4">
          {Array.from(grouped.entries()).map(([category, items]) => (
            <section key={category}>
              <h3 className="text-xs uppercase tracking-wide opacity-60 mb-1">{category}</h3>
              <ul className="space-y-1">
                {items.map((s) => (
                  <li key={s.keys} className="flex items-center justify-between text-sm">
                    <span className="opacity-80">{s.description}</span>
                    <kbd className="font-mono text-xs px-2 py-0.5 rounded bg-[var(--color-munya-sand)]/40 border border-[var(--color-munya-sand)]">
                      {s.keys}
                    </kbd>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <p className="text-[10px] opacity-50 mt-4">
          Los atajos no funcionan mientras escribes en un campo. Pulsa{' '}
          <kbd className="font-mono">?</kbd> de nuevo para cerrar.
        </p>
      </div>
    </div>
  );
}
